'use client';

import { useSuspenseQuery } from '@apollo/client/react';
import { FC, PropsWithChildren, useCallback, useMemo, useState } from 'react';

import { GET_ME_IMAGE_GENERATIONS } from '@/.graphql';
import { SerializedFile, serializeFiles } from '@/src/utils/serializeFile';

import { createCustomContext } from './context';

interface IImageGenerationProps {
  images: SerializedFile[];
  posts: SerializedFile[];
  activeImageFile: File | null;
  setActiveImageFile: (file: File | null) => void;
  activeImage: SerializedFile;
  setActiveImage: (image: SerializedFile) => void;
  loadMoreImages: () => void;
  hasMoreImages: boolean;
  isLoadingImages: boolean;
  getImageById: (id: string) => SerializedFile | undefined;
}

const [ImageContext, useImageGenerationContext] =
  createCustomContext<IImageGenerationProps>({
    name: 'ImageGenerationContext',
    strict: false,
  });

export { useImageGenerationContext };

export const ImageGenerationProvider: FC<PropsWithChildren> = ({
  children,
}) => {
  const {
    data: imageData,
    fetchMore: fetchMoreImages,
    networkStatus: imageNetworkStatus,
  } = useSuspenseQuery(GET_ME_IMAGE_GENERATIONS, {
    variables: {
      cursor: null,
      isPublished: null,
    },
  });

  // Мемоизируем сериализованные данные
  const images = useMemo(
    () => serializeFiles(imageData?.getMeImageGenerations?.data),
    [imageData],
  );

  const posts = useMemo(
    () => images.filter(({ isPublished }) => isPublished),
    [images],
  );

  // Получаем курсор
  const imageNextCursor = imageData?.getMeImageGenerations?.nextCursor ?? null;
  const hasMoreImages = !!imageNextCursor;

  // Статус загрузки для fetchMore
  const isLoadingImages = imageNetworkStatus === 3; // NetworkStatus.fetchMore

  const loadMoreImages = useCallback(() => {
    if (!hasMoreImages || isLoadingImages) {
      return;
    }

    return fetchMoreImages({
      variables: {
        cursor: imageNextCursor,
        isPublished: null,
      },
    });
  }, [hasMoreImages, isLoadingImages, imageNextCursor, fetchMoreImages]);

  const [activeImage, setActiveImage] = useState<SerializedFile>(
    images[0] || { id: '', src: '', isVideo: false },
  );
  const [activeImageFile, setActiveImageFile] = useState<File | null>(null);

  // Utility functions
  const getImageById = useCallback(
    (id: string) =>
      images.find(
        (img) => 'imageGenerationId' in img && img.imageGenerationId === id,
      ),
    [images],
  );

  const contextValue = useMemo(
    () => ({
      images,
      posts,
      activeImage,
      setActiveImage,
      loadMoreImages,
      hasMoreImages,
      isLoadingImages,
      getImageById,
      activeImageFile,
      setActiveImageFile,
    }),
    [
      images,
      posts,
      activeImage,
      setActiveImage,
      loadMoreImages,
      hasMoreImages,
      isLoadingImages,
      getImageById,
      activeImageFile,
      setActiveImageFile,
    ],
  );

  return <ImageContext value={contextValue}>{children}</ImageContext>;
};
